import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from "../../services/api";
import { exportRelationshipToExcel } from "../../utils/reportExport";
import ScopeFiltersPanel from "./ScopeFiltersPanel";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const RelationshipReportViewer = ({ reportId, report, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [selectedDepth, setSelectedDepth] = useState("all");
  const [exporting, setExporting] = useState(false);

  // Scope filters state
  const [scopeFilters, setScopeFilters] = useState({
    filterOrganizations: [],
    filterInitiatives: [],
    filterCapabilities: [],
    filterContexts: [],
  });
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [savingFilters, setSavingFilters] = useState(false);

  const reportConfig = report?.configuration || {};

  // Handle Excel export
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      exportRelationshipToExcel(report, data);
    } catch (err) {
      debugError("Export error:", err);
      setError("Failed to export report: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Load saved filters from report configuration
  useEffect(() => {
    if (reportConfig.scopeFilters) {
      setScopeFilters(reportConfig.scopeFilters);
    }
  }, [report?.id]);

  useEffect(() => {
    executeReport();
  }, [reportId, report?.id]);

  // Re-execute report when scope filters change (after initial load)
  const executeReportWithFilters = useCallback(
    async (filters) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(
          `/api/reports/${reportId || report.id}/execute`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scope_filters: filters,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to execute report");
        }

        const result = await response.json();
        if (result.success && result.result) {
          setData(result.result);
          // Expand first source by default
          if (result.result.data?.results?.length > 0) {
            setExpandedSources(
              new Set([result.result.data.results[0].source.id])
            );
          }
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        debugError("Error executing report:", err);
        setError(err.message || "Failed to execute report");
      } finally {
        setLoading(false);
      }
    },
    [reportId, report?.id]
  );

  const executeReport = async () => {
    await executeReportWithFilters(scopeFilters);
  };

  // Handle scope filter changes
  const handleScopeFiltersChange = useCallback(
    (newFilters) => {
      setScopeFilters(newFilters);
      setFiltersDirty(true);
      // Re-execute report with new filters
      executeReportWithFilters(newFilters);
    },
    [executeReportWithFilters]
  );

  // Save filters to report configuration
  const saveFilters = async () => {
    if (!report?.id) return;

    setSavingFilters(true);
    try {
      const updatedConfig = {
        ...reportConfig,
        scopeFilters: scopeFilters,
      };

      const response = await apiFetch(`/api/reports/${report.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          configuration: updatedConfig,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save filters");
      }

      setFiltersDirty(false);
    } catch (err) {
      debugError("Error saving filters:", err);
      setError("Failed to save filters: " + err.message);
    } finally {
      setSavingFilters(false);
    }
  };

  const toggleSource = (sourceId) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  };

  const getVariantTitle = () => {
    switch (data?.variant) {
      case "impact-analysis":
        return "Impact Analysis";
      case "dependency":
        return "Dependency Report";
      case "matrix":
        return "Relationship Matrix";
      default:
        return "Relationship Analysis";
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === "downstream" ? "→" : "←";
  };

  const getDirectionColor = (direction) => {
    return direction === "downstream" ? "text-orange-400" : "text-blue-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8">
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin w-12 h-12 text-blue-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-300 text-lg">Loading report...</p>
            <p className="text-gray-500 text-sm mt-2">
              Analyzing relationships...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-6">
            <h3 className="text-red-200 font-semibold mb-2">
              Error Loading Report
            </h3>
            <p className="text-red-300">{error}</p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={executeReport}
                className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Rerun
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Back to Reports
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const variant = data?.variant || "impact-analysis";

  // Render different views based on variant
  const renderContent = () => {
    switch (variant) {
      case "impact-analysis":
      case "dependency":
        return renderImpactView();
      case "matrix":
        return renderMatrixView();
      default:
        return renderImpactView();
    }
  };

  const renderImpactView = () => {
    const results = data?.data?.results || [];

    // Filter results
    const filteredResults = results.filter((result) => {
      if (!filterText) return true;
      const lowerFilter = filterText.toLowerCase();
      return (
        result.source.name.toLowerCase().includes(lowerFilter) ||
        result.impacts.some((i) => i.name.toLowerCase().includes(lowerFilter))
      );
    });

    return (
      <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Source Entities</div>
            <div className="text-2xl font-bold text-white">
              {summary.total_source_entities || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Total Impacted</div>
            <div className="text-2xl font-bold text-orange-400">
              {summary.total_impacted || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Analysis Depth</div>
            <div className="text-2xl font-bold text-blue-400">
              {data?.analysis_depth || 2} hops
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Direction</div>
            <div className="text-2xl font-bold text-purple-400 capitalize">
              {data?.analysis_direction || "both"}
            </div>
          </div>
        </div>

        {/* Impacts by Type */}
        {Object.keys(summary.impacted_by_type || {}).length > 0 && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Impacts by Entity Type
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(summary.impacted_by_type || {}).map(
                ([type, count]) => (
                  <div key={type} className="bg-gray-900 rounded-lg px-4 py-2">
                    <span className="text-gray-400">{type}:</span>
                    <span className="text-white font-bold ml-2">{count}</span>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search entities..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <select
              value={selectedDepth}
              onChange={(e) => setSelectedDepth(e.target.value)}
              className="px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
            >
              <option value="all">All depths</option>
              {Object.keys(summary.impacted_by_depth || {}).map((d) => (
                <option key={d} value={d}>
                  Depth {d} only
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {filteredResults.map((result) => (
            <div
              key={result.source.id}
              className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden"
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-700/50"
                onClick={() => toggleSource(result.source.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">
                    {expandedSources.has(result.source.id) ? "▼" : "▶"}
                  </span>
                  <div>
                    <span className="font-medium text-white">
                      {result.source.name}
                    </span>
                    <span className="text-gray-400 text-sm ml-2">
                      ({result.source.type})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-orange-400 font-bold">
                    {result.total_impacted} impacted
                  </span>
                </div>
              </div>

              {expandedSources.has(result.source.id) && (
                <div className="border-t border-gray-700 px-4 py-3">
                  {result.total_impacted === 0 ? (
                    <p className="text-gray-400">No impacted entities found</p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(result.impacts_by_depth || {}).map(
                        ([depth, impacts]) => {
                          if (
                            selectedDepth !== "all" &&
                            selectedDepth !== depth
                          )
                            return null;
                          const filteredImpacts = filterText
                            ? impacts.filter((i) =>
                                i.name
                                  .toLowerCase()
                                  .includes(filterText.toLowerCase())
                              )
                            : impacts;

                          if (filteredImpacts.length === 0) return null;

                          return (
                            <div key={depth}>
                              <h4 className="text-sm font-medium text-gray-400 mb-2">
                                Depth {depth} ({filteredImpacts.length}{" "}
                                entities)
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {filteredImpacts.map((impact, idx) => (
                                  <div
                                    key={`${impact.id}-${idx}`}
                                    className="bg-gray-900 rounded px-3 py-2 flex items-center justify-between"
                                  >
                                    <div>
                                      <span className="text-white">
                                        {impact.name}
                                      </span>
                                      <span className="text-gray-500 text-xs ml-2">
                                        ({impact.type})
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`text-xs ${getDirectionColor(impact.direction)}`}
                                      >
                                        {getDirectionIcon(impact.direction)}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {impact.relationship_type}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderMatrixView = () => {
    const { sources, targets, matrix } = data?.data || {};

    if (!sources || !targets || !matrix) {
      return <p className="text-gray-400">No matrix data available</p>;
    }

    return (
      <>
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">
              {data?.source_entity_type}
            </div>
            <div className="text-2xl font-bold text-white">
              {summary.source_count || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">
              {data?.target_entity_type}
            </div>
            <div className="text-2xl font-bold text-white">
              {summary.target_count || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Connected</div>
            <div className="text-2xl font-bold text-green-400">
              {summary.connected_cells || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Coverage</div>
            <div className="text-2xl font-bold text-blue-400">
              {summary.coverage_percentage || 0}%
            </div>
          </div>
        </div>

        {/* Matrix Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase bg-gray-900 sticky left-0">
                    {data?.source_entity_type}
                  </th>
                  {targets.map((target) => (
                    <th
                      key={target.id}
                      className="px-3 py-3 text-center text-xs font-medium text-gray-400 bg-gray-900"
                    >
                      <div className="w-24 truncate" title={target.name}>
                        {target.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sources.map((source) => (
                  <tr key={source.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm text-white font-medium bg-gray-800 sticky left-0">
                      <div className="w-40 truncate" title={source.name}>
                        {source.name}
                      </div>
                    </td>
                    {targets.map((target) => {
                      const cell = matrix[source.id]?.connections[target.id];
                      return (
                        <td key={target.id} className="px-3 py-3 text-center">
                          {cell?.connected ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-900 text-green-400">
                              {cell.count > 1 ? cell.count : "✓"}
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white mb-2 flex items-center gap-1 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Reports
            </button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {report?.name || getVariantTitle()}
              {filtersDirty && (
                <span className="text-xs bg-yellow-600 text-yellow-100 px-2 py-0.5 rounded-full font-normal">
                  Unsaved filters
                </span>
              )}
            </h1>
            <p className="text-gray-400 mt-1">
              {getVariantTitle()}
              {data?.generated_at &&
                ` • Generated: ${new Date(data.generated_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Scope Filters */}
            <ScopeFiltersPanel
              scopeFilters={scopeFilters}
              onFiltersChange={handleScopeFiltersChange}
              onSaveFilters={saveFilters}
              filtersDirty={filtersDirty}
              savingFilters={savingFilters}
            />
            {/* Export to Excel Button */}
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
            >
              {exporting ? (
                <svg
                  className="animate-spin w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              )}
              Export to Excel
            </button>
            <button
              onClick={executeReport}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Content based on variant */}
        {renderContent()}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-400">
          {(variant === "impact-analysis" || variant === "dependency") && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-orange-400">→</span>
                <span>Downstream (depends on source)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-400">←</span>
                <span>Upstream (source depends on)</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelationshipReportViewer;
